import { Document, HeadingLevel, Packer, Paragraph, TextRun } from 'docx'
import { marked } from 'marked'

export const downloadDocx = async (
  markdown: string,
  fileName = 'document.docx',
) => {
  const tokens = marked.lexer(markdown)
  const paragraphs: Paragraph[] = []

  const parseInlineTokens = (text: string): TextRun[] => {
    const inline = marked.lexer(text, { gfm: true }) as any
    const output: TextRun[] = []

    // This may be a paragraph block, so we dig into .tokens if present
    const inlineTokens = inline[0]?.tokens || []

    for (const token of inlineTokens) {
      switch (token.type) {
        case 'text':
          output.push(new TextRun(token.text))
          break
        case 'strong':
          output.push(new TextRun({ text: token.text, bold: true }))
          break
        case 'em':
          output.push(new TextRun({ text: token.text, italics: true }))
          break
        case 'codespan':
          output.push(new TextRun({ text: token.text, font: 'Courier New' }))
          break
        case 'link':
          output.push(
            new TextRun({
              text: token.text,
              style: 'Hyperlink',
            }),
          )
          break
        default:
          output.push(new TextRun(token.raw || token.text))
      }
    }

    return output
  }

  for (const token of tokens) {
    if (token.type === 'heading') {
      paragraphs.push(
        new Paragraph({
          spacing: { before: 200 },
          children: parseInlineTokens(token.text),
          heading:
            HeadingLevel[
              `HEADING_${token.depth}` as keyof typeof HeadingLevel
            ] || HeadingLevel.HEADING_1,
        }),
      )
    } else if (token.type === 'paragraph') {
      paragraphs.push(
        new Paragraph({
          children: parseInlineTokens(token.text),
        }),
      )
    } else if (token.type === 'list') {
      for (const item of token.items) {
        paragraphs.push(
          new Paragraph({
            children: parseInlineTokens(item.text),
            bullet: { level: 0 },
          }),
        )
      }
    } else if (token.type === 'code') {
      paragraphs.push(
        new Paragraph({
          children: [new TextRun({ text: token.text, font: 'Courier New' })],
        }),
      )
    } else if (token.type === 'blockquote') {
      paragraphs.push(
        new Paragraph({
          children: parseInlineTokens(token.text),
          border: {
            left: { color: '999999', space: 4, size: 6, style: 'single' },
          },
        }),
      )
    }
  }

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: 'Arial',
            size: 24, // 12pt
          },
        },
      },
    },
    sections: [{ children: paragraphs }],
  })

  const blob = await Packer.toBlob(doc)
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName
  link.click()
  URL.revokeObjectURL(link.href)
}

const downloadPDF = async (markdown: string, fileName = 'document.pdf') => {
  const { marked } = await import('marked')
  const html2pdf = (await import('html2pdf.js')).default

  const html = marked(markdown)

  const container = document.createElement('div')
  container.innerHTML = `
    <html>
      <head>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            padding: 48px;
            color: #333;
          }
          h1, h2, h3, h4, h5, pre, blockquote, table, ul, ol, p {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          p {
            margin: 1em 0;
          }
          ul, ol {
            margin: 1em 0;
            padding-left: 1.5em;
          }
          pre {
            background: #f6f8fa;
            padding: 12px;
            overflow-x: auto;
          }
          code {
            background: #f6f8fa;
            padding: 2px 4px;
            font-family: monospace;
            font-size: 90%;
          }
          blockquote {
            border-left: 4px solid #ccc;
            padding-left: 16px;
            color: #666;
            margin: 1em 0;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            margin: 1em 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
          }
          th {
            background: #f2f2f2;
            font-weight: 600;
          }
        </style>
      </head>
      <body>${html}</body>
    </html>
  `

  document.body.appendChild(container)

  await html2pdf()
    .from(container)
    .set({
      filename: fileName,
      margin: [20, 20, 20, 20],
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    })
    .save()

  document.body.removeChild(container)
}

export const DocumentHelper = {
  downloadDocx,
  downloadPDF,
}
